/**
 * Where-was-I history.
 *
 * Some actions destroy the page you're standing on — archiving an order is the
 * canonical one — so the app has to send you somewhere afterwards. It used to
 * send everyone to the dashboard, which threw away the context you came from:
 * open an order from a customer, archive it, and you'd have to navigate back to
 * that customer from scratch.
 *
 * This keeps a short visit log in sessionStorage (per tab, cleared when the tab
 * closes) so those redirects can land on the last *different* page you were on.
 *
 * It is deliberately NOT the browser history stack: pressing Back appends here
 * rather than popping, because the question we answer is "what were you looking
 * at before this?", not "what would Back do?". We don't use `router.back()` for
 * the same reason plus a harder one — on a deep link (email, bookmark, new tab)
 * Back leaves the app entirely.
 */

const KEY = 'tc_nav_history';
const MAX_ENTRIES = 15;

const read = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (entries) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    // Private-mode / quota failures are not worth breaking navigation over.
  }
};

/**
 * Record a visited page.
 *
 * @param {string} url   the as-path actually shown (`/acme/customers/12?tab=orders`)
 * @param {string} route the Next route pattern (`/[slug]/customers/[uuid]`) —
 *                       this is what lets a caller skip "pages like the one I'm
 *                       on" without string-matching ids.
 */
export function recordRoute(url, route) {
  if (!isInAppPath(url)) return;
  const entries = read();
  const last = entries[entries.length - 1];
  // A refresh, a shallow query-only re-push, or a hash nav shouldn't stack up.
  if (last && last.url === url) return;
  entries.push({ url, route: route || url });
  write(entries.slice(-MAX_ENTRIES));
}

/**
 * The most recent page that isn't the one being left behind.
 *
 * Walks the log backwards, skipping every entry whose route matches the current
 * page (so order → order → archive still returns to the customer that led there)
 * and anything else the caller excludes. Returns `fallback` when there's nothing
 * usable — a deep link straight into the page, a fresh tab, wiped storage.
 *
 * @param {object}   opts
 * @param {string}   opts.currentRoute   `router.pathname` of the page being left
 * @param {string}   opts.currentUrl     `router.asPath` of the page being left
 * @param {string}   opts.fallback       where to go when there's no history
 * @param {string[]} opts.excludeRoutes  additional route patterns to skip
 */
export function getReturnPath({
  currentRoute,
  currentUrl,
  fallback,
  excludeRoutes = [],
} = {}) {
  const entry = getReturnEntry({ currentRoute, currentUrl, excludeRoutes });
  return entry ? entry.url : fallback;
}

/**
 * True only for a relative in-app path: `/x…`. Never `javascript:`/`data:` or
 * an absolute URL (no leading `/`), never protocol-relative `//host`, never
 * `/\host` (browsers fold `\` to `/`, so it is `//host` too), and never one
 * carrying control characters (browsers strip tab/newline, so `/\t/host`
 * becomes `//host`). Shared by the visit log and every query-supplied
 * destination such as the order page's `returnTo`
 * (app/orders/orderdetail-returnto-unvalidated-back-link-href).
 */
export function isInAppPath(url) {
  return (
    typeof url === 'string' &&
    url.startsWith('/') &&
    url[1] !== '/' &&
    url[1] !== '\\' &&
    // Control characters: U+0000–U+001F and U+007F.
    !Array.from(url).some((ch) => {
      const code = ch.charCodeAt(0);
      return code < 0x20 || code === 0x7f;
    })
  );
}

/**
 * Same walk as `getReturnPath`, but hands back the whole `{url, route}` entry.
 *
 * A redirect only needs somewhere to go, so `getReturnPath` is enough for it.
 * A *back button* also has to name the place — "Back to status report" — and the
 * stored route pattern is what survives ids and query strings, so it's the thing
 * to label from. Returns `null` when there's no usable history, which callers
 * showing an affordance should read as "don't offer one" rather than inventing
 * a destination the user was never at.
 */
export function getReturnEntry({
  currentRoute,
  currentUrl,
  excludeRoutes = [],
} = {}) {
  const skip = new Set([currentRoute, ...excludeRoutes].filter(Boolean));
  const entries = read();

  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    const url = entry?.url;
    // Relative in-app paths only — never hand an external or malformed value
    // to router.push. `//host` is protocol-relative, i.e. off-site.
    if (!isInAppPath(url)) {
      continue;
    }
    if (skip.has(entry.route)) continue;
    if (currentUrl && url === currentUrl) continue;
    return { url, route: entry.route || url };
  }

  return null;
}

// The order/quote record page. Leaving it by Back is what a list or report
// treats as "coming back" (see markReturnNavigation).
export const ORDER_DETAIL_ROUTE = '/[slug]/orders/[uuid]';

const RETURN_KEY = 'tc_nav_return';
// Long enough for a slow page to load and mount its filters; short enough that
// a marker no page read can't resurface on a later, unrelated visit.
const RETURN_TTL_MS = 60 * 1000;

const pathOnly = (url) => String(url).split(/[?#]/)[0];

/**
 * Mark that the user is going back to `url` from an order — the order page's
 * back link, or the browser's Back button (wired in _app.js).
 *
 * The pages with Presets (reports, customer Orders tab, Orders/Quotes lists)
 * restore their last filters and preset only on this explicit return. Any
 * other visit — sidebar, a link, a reload — opens on the page's defaults.
 */
export function markReturnNavigation(url) {
  if (typeof window === 'undefined' || typeof url !== 'string') return;
  try {
    window.sessionStorage.setItem(
      RETURN_KEY,
      JSON.stringify({ path: pathOnly(url), at: Date.now() })
    );
  } catch {
    // Without storage the page just opens with its defaults.
  }
}

/**
 * True, once, when the page at `pathname` was reached through a return marked
 * by `markReturnNavigation`. Consumes the marker.
 */
export function consumeReturnNavigation(pathname) {
  if (typeof window === 'undefined') return false;
  try {
    const marker = JSON.parse(window.sessionStorage.getItem(RETURN_KEY));
    if (!marker) return false;
    const fresh = Date.now() - Number(marker.at) < RETURN_TTL_MS;
    const matches = fresh && marker.path === pathOnly(pathname);
    if (matches || !fresh) window.sessionStorage.removeItem(RETURN_KEY);
    return matches;
  } catch {
    return false;
  }
}

/**
 * The marker for a return to `pathname`, without consuming it: its timestamp,
 * or null when this page wasn't reached by a marked return. For state that
 * several pieces of one page restore separately (useHistoryEntryValue), where
 * a single consumer can't speak for the rest — and where the page's Presets,
 * mounting later, still need to consume it. Callers use each marker (by its
 * timestamp) once.
 */
export function peekReturnNavigation(pathname) {
  if (typeof window === 'undefined') return null;
  try {
    const marker = JSON.parse(window.sessionStorage.getItem(RETURN_KEY));
    if (!marker) return null;
    const at = Number(marker.at);
    const fresh = Date.now() - at < RETURN_TTL_MS;
    return fresh && marker.path === pathOnly(pathname) ? at : null;
  } catch {
    return null;
  }
}

// Route pattern → how a back button should name it. Only the destinations that
// actually lead into a record page need an entry; anything else falls through
// to the generic wording rather than guessing a noun from the URL.
const ROUTE_LABELS = {
  '/[slug]/orders': 'orders',
  '/[slug]/quotes': 'quotes',
  '/[slug]/customers': 'customers',
  '/[slug]/customers/[uuid]': 'customer',
  '/[slug]/dashboard': 'dashboard',
  '/[slug]/notifications': 'notifications',
  '/[slug]/deliveries': 'deliveries',
  '/[slug]/reports': 'reports',
};

/**
 * Human wording for the page a back button would return to.
 *
 * Reports are the case that matters and the case that grows: every tab under
 * `/[slug]/reports/*` becomes "<name> report" from the pattern itself, so a new
 * report page is labelled correctly the day it's added without touching a map.
 *
 * @param {string} route a stored route pattern (`/[slug]/reports/status`)
 * @returns {string} label to follow "Back to"
 */
export function describeReturnRoute(route) {
  if (typeof route !== 'string' || !route) return 'previous page';
  if (ROUTE_LABELS[route]) return ROUTE_LABELS[route];

  const reportMatch = route.match(/^\/\[slug\]\/reports\/([^/[]+)$/);
  if (reportMatch) return `${reportMatch[1].replace(/-/g, ' ')} report`;

  return 'previous page';
}
